import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ChevronRight, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import logoHorizontal from "@/assets/logo-horizontal.webp";
import ApplyLink from "@/components/ApplyLink";
import RevealContact from "@/components/RevealContact";
import HilerIQLink from "@/components/HilerIQLink";
import { HILERIQ_LABEL } from "@/lib/hileriq";

interface SubLink {
  label: string;
  desc: string;
  path: string;
  children?: { label: string; desc?: string; path: string; group?: string }[];
}

interface NavItem {
  label: string;
  navLabel?: string;
  path: string;
  children?: SubLink[];
}

const loanPrograms = [
  { label: "All Loan Programs", desc: "See every mortgage option in one place", path: "/loan-programs" },
  { label: "Conventional", path: "/loan-programs/conventional", group: "Common Options" },
  { label: "FHA", path: "/loan-programs/fha", group: "Common Options" },
  { label: "VA", path: "/loan-programs/va", group: "Common Options" },
  { label: "USDA", path: "/loan-programs/usda", group: "Common Options" },
  { label: "Jumbo", path: "/loan-programs/jumbo", group: "Common Options" },
  { label: "Refinance", path: "/refinance", group: "Common Options" },
  { label: "Flexible Mortgage Options", desc: "For self-employed buyers, investors, and ITIN borrowers", path: "/non-qm-loans-minnesota", group: "Flexible Options" },
  { label: "Bank Statement Loans", path: "/loan-programs/bank-statement-loans", group: "Flexible Options" },
  { label: "DSCR Loans", path: "/loan-programs/dscr", group: "Flexible Options" },
  { label: "ITIN Loans", path: "/loan-programs/itin-loans", group: "Flexible Options" },
  { label: "Doctor Loans", path: "/loan-programs/doctor-loans", group: "Flexible Options" },
];

const navLinks: NavItem[] = [
  {
    label: "Buy a Home",
    path: "/buy",
    children: [
      { label: "Mortgage Pre-Approval", desc: "Get qualified before you start shopping", path: "/buy/pre-approval" },
      { label: "Affordability Calculator", desc: "Find out how much home you can afford", path: "/calculators/affordability" },
      { label: "First-Time Buyers", desc: "Learn the basics of buying your first home", path: "/buy/first-time-buyers" },
      { label: "Down Payment Options", desc: "Explore low and no down-payment programs", path: "/buy/down-payment-options" },
      { label: "Down Payment Assistance", desc: "How Minnesota assistance programs actually work", path: "/buy/down-payment-assistance" },
      { label: "Flexible Mortgage Options", desc: "For self-employed buyers, investors, and ITIN borrowers", path: "/non-qm-loans-minnesota" },
      { label: "Loan Programs", desc: "Compare Conventional, FHA, VA, USDA & Jumbo", path: "/loan-programs", children: loanPrograms },
    ],
  },
  {
    label: "Refinance",
    path: "/refinance",
    children: [
      { label: "Lower Your Payment", desc: "Reduce your monthly mortgage cost", path: "/refinance/lower-payment" },
      { label: "Cash-Out Refinance", desc: "Tap into your home equity for cash", path: "/refinance/cash-out" },
      { label: "Shorten Your Term", desc: "Pay off your home faster and save on interest", path: "/refinance/shorten-term" },
    ],
  },
  {
    label: "Rates",
    path: "/rates",
    children: [
      { label: "Today's Mortgage Rates", desc: "See current rates updated daily", path: "/rates/today" },
      { label: "Understanding Mortgage Rates", desc: "Learn what affects mortgage pricing", path: "/rates/understanding-rates" },
      { label: "When to Lock Your Rate", desc: "Know the right time to secure your rate", path: "/rates/when-to-lock" },
      { label: "Loan Estimate Second Look", desc: "Review your rate, fees, cash to close, and loan structure", path: "/second-look-loan-estimate" },
    ],
  },
  {
    label: "Calculators",
    path: "/calculators",
    children: [
      { label: "Payment Calculator", desc: "Estimate your monthly payment in seconds", path: "/calculators/mortgage-payment" },
      { label: "Affordability", desc: "Find out how much home you can afford", path: "/calculators/affordability" },
      { label: "Refinance Savings", desc: "See how much you could save by refinancing", path: "/calculators/refinance" },
      { label: "Rent vs Buy", desc: "Compare renting and buying side by side", path: "/calculators/rent-vs-buy" },
      { label: "DSCR Calculator", desc: "Estimate DSCR and cash flow on a rental property", path: "/calculators/dscr" },
    ],
  },
  {
    label: "Guides",
    path: "/resources",
    children: [
      { label: "First-Time Buyer Guide", desc: "Everything you need to know before you buy", path: "/learn/first-time-buyer-guide" },
      { label: "What Is a Mortgage Broker?", desc: "How brokers compare loan options across lenders", path: "/learn/what-is-a-mortgage-broker" },
      { label: "Understanding Mortgage Rates", desc: "Learn what affects mortgage pricing", path: "/rates/understanding-rates" },
      { label: "Pre-Approval Explained", desc: "Why pre-approval matters and how to get it", path: "/buy/pre-approval" },
      { label: "Loan Estimate Second Look", desc: "Review your rate, fees, cash to close, and loan structure", path: "/second-look-loan-estimate" },
      { label: "Understanding Closing Costs", desc: "Know what to expect at the closing table", path: "/learn/closing-costs" },
      { label: "Loan Programs Overview", desc: "Compare Conventional, FHA, VA, Jumbo & USDA", path: "/loan-programs" },
    ],
  },
  {
    label: "About",
    path: "/about",
    children: [
      { label: "About Hiler Mortgage", desc: "Our story and what sets us apart", path: "/about" },
      { label: "Meet the Team", desc: "The people behind your mortgage experience", path: "/about/team" },
      { label: "Contact Us", desc: "Get in touch — we're here to help", path: "/about/contact" },
    ],
  },
  {
    label: "For Real Estate Agents",
    navLabel: "For Agents",
    path: "/real-estate-agents",
    children: [
      { label: "Agent Overview", desc: "Where to start as a real estate agent", path: "/real-estate-agents" },
      { label: "HilerIQ Tools", desc: "Tools for all real estate agents", path: "/real-estate-agents/hileriq" },
      { label: "Active Loan Experience", desc: "Visibility on active Hiler Mortgage files", path: "/real-estate-agents/active-loans" },
      { label: "Buyer Transaction Coordination", desc: "Human coordination for purchase borrowers", path: "/real-estate-agents/transaction-coordination" },
      { label: "Agent Services", desc: "HilerTC drafting and compliance support", path: "/real-estate-agents/services" },
    ],
  },
];

const DropdownNav = ({ item, pathname }: { item: NavItem; pathname: string }) => {
  const [open, setOpen] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  const isActive = pathname === item.path || pathname.startsWith(item.path + "/");

  const enter = () => {
    clearTimeout(timeout.current);
    setOpen(true);
  };
  const leave = () => {
    timeout.current = setTimeout(() => setOpen(false), 150);
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
    }
    if (e.key === "Enter" || e.key === " ") {
      if (!open && item.children) {
        e.preventDefault();
        setOpen(true);
      }
    }
  }, [open, item.children]);

  return (
    <div className="relative" onMouseEnter={enter} onMouseLeave={leave} ref={containerRef}>
      <Link
        to={item.path}
        className={`font-body text-[15px] font-medium tracking-normal transition-colors duration-300 whitespace-nowrap flex items-center gap-1.5 ${
          isActive
            ? "text-primary-foreground"
            : "text-on-dark-secondary hover:text-primary-foreground"
        }`}
        aria-haspopup={item.children ? "true" : undefined}
        aria-expanded={item.children ? open : undefined}
        onKeyDown={handleKeyDown}
        onFocus={enter}
        onBlur={(e) => {
          if (!containerRef.current?.contains(e.relatedTarget as Node)) {
            leave();
          }
        }}
      >
        {item.navLabel ?? item.label}
        {item.children && <ChevronDown className="h-3 w-3 opacity-50" aria-hidden="true" />}
      </Link>

      {item.children && open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3">
          {/* Plain link list, not an ARIA menu: these navigate rather than act,
              so link semantics keep the accessibility tree well-formed. */}
          <ul
            className="bg-primary/95 backdrop-blur-2xl border border-primary-foreground/10 rounded-lg shadow-xl shadow-primary/30 py-2 min-w-[280px] list-none"
            aria-label={`${item.label} submenu`}
          >
            {item.children.map((child) => (
              <DropdownItem
                key={child.path}
                child={child}
                pathname={pathname}
                onClose={() => setOpen(false)}
                parentRef={containerRef}
              />
            ))}
          </ul>
        </div>
      )}

    </div>
  );
};

const DropdownItem = ({ child, pathname, onClose, parentRef }: {
  child: SubLink; pathname: string; onClose: () => void;
  parentRef: React.RefObject<HTMLDivElement>;
}) => {
  const [subOpen, setSubOpen] = useState(false);
  const subTimeout = useRef<ReturnType<typeof setTimeout>>();
  const itemRef = useRef<HTMLLIElement>(null);

  const enterSub = () => { clearTimeout(subTimeout.current); setSubOpen(true); };
  const leaveSub = () => { subTimeout.current = setTimeout(() => setSubOpen(false), 150); };

  if (child.children) {
    return (
      <li className="relative" onMouseEnter={enterSub} onMouseLeave={leaveSub} ref={itemRef}>
        <Link
          to={child.path}
          onClick={onClose}
          aria-haspopup="true"
          aria-expanded={subOpen}

          onFocus={enterSub}
          onBlur={(e) => {
            if (!itemRef.current?.contains(e.relatedTarget as Node)) {
              leaveSub();
            }
          }}
          className={`flex items-center justify-between px-4 py-3 transition-colors duration-200 ${
            pathname === child.path || pathname.startsWith(child.path + "/")
              ? "bg-primary-foreground/8"
              : "hover:bg-primary-foreground/5"
          }`}
        >
          <div>
            <span className={`block font-body text-[14px] font-semibold ${
              pathname === child.path ? "text-primary-foreground" : "text-primary-foreground/70"
            }`}>
              {child.label}
            </span>
            <span className="block font-body text-sm text-on-dark-tertiary mt-0.5 leading-snug">
              {child.desc}
            </span>
          </div>
          <ChevronRight className="h-3.5 w-3.5 text-on-dark-tertiary flex-shrink-0 ml-2" aria-hidden="true" />
        </Link>
        {subOpen && (
          <div className="absolute left-full top-0 pl-1">
            <ul
              className="bg-primary/95 backdrop-blur-2xl border border-primary-foreground/10 rounded-lg shadow-xl shadow-primary/30 py-2 min-w-[280px] list-none"
              aria-label={`${child.label} submenu`}
            >
              {child.children.map((sub, i) => {
                const prevGroup = i > 0 ? (child.children![i - 1] as any).group : undefined;
                const showGroup = (sub as any).group && (sub as any).group !== prevGroup;
                return (
                <li key={sub.path}>
                  {showGroup && (
                    <div className="px-4 pt-3 pb-1 mt-1 border-t border-primary-foreground/10 first:border-t-0 first:mt-0">
                      <span className="font-body text-[10px] font-semibold uppercase tracking-[0.2em] text-on-dark-disclosure">
                        {(sub as any).group}
                      </span>
                    </div>
                  )}
                  <Link
                  to={sub.path}
                  onClick={onClose}
                  onBlur={(e) => {
                    if (!itemRef.current?.contains(e.relatedTarget as Node)) {
                      leaveSub();
                    }
                  }}
                  className={`block px-4 py-2.5 transition-colors duration-200 ${
                    pathname === sub.path
                      ? "bg-primary-foreground/8"
                      : "hover:bg-primary-foreground/5"
                  }`}
                >
                  <span className={`block font-body text-[14px] font-medium ${
                    pathname === sub.path ? "text-primary-foreground" : "text-primary-foreground/60"
                  }`}>
                    {sub.label}
                  </span>
                  {'desc' in sub && (sub as any).desc && (
                    <span className="block font-body text-[12px] text-on-dark-disclosure mt-0.5 leading-snug">
                      {(sub as any).desc}
                    </span>
                  )}
                  </Link>
                </li>
                );
              })}
            </ul>
          </div>
        )}
      </li>
    );
  }


  return (
    <li>
      <Link
        to={child.path}
        onClick={onClose}
        className={`block px-4 py-3 transition-colors duration-200 ${
          pathname === child.path
            ? "bg-primary-foreground/8"
            : "hover:bg-primary-foreground/5"
        }`}
      >
        <span className={`block font-body text-[14px] font-semibold ${
          pathname === child.path
            ? "text-primary-foreground"
            : "text-primary-foreground/70"
        }`}>
          {child.label}
        </span>
        <span className="block font-body text-sm text-on-dark-tertiary mt-0.5 leading-snug">
          {child.desc}
        </span>
      </Link>
    </li>
  );

};

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setOpen(false);
    setMobileExpanded(null);
  }, [location.pathname]);

  // Trap focus / handle escape in mobile menu
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  const toggleMobileExpand = (path: string) => {
    setMobileExpanded(mobileExpanded === path ? null : path);
  };

  return (
    <>
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:text-sm focus:font-semibold focus:shadow-lg"
    >
      Skip to main content
    </a>
    <header
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-primary/95 backdrop-blur-2xl shadow-lg shadow-primary/20"
          : "bg-primary"
      }`}
      role="banner"
    >
      {/* Top utility bar — large desktop only */}
      <div className="hidden xl:block border-b border-primary-foreground/10 bg-navy-deep/60">
        <div className="mx-auto flex h-9 max-w-[1440px] items-center justify-end gap-8 px-6 lg:px-10">
          <RevealContact
            type="phone"
            encoded="dGVsOisxNzYzMzQzNjMwMA=="
            hideHelper
            iconClassName="h-3.5 w-3.5"
            className="font-body text-[13px] font-medium text-on-dark-tertiary hover:text-primary-foreground transition-colors duration-300 whitespace-nowrap"
          />
          <HilerIQLink className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 font-body text-[13px] font-medium text-on-dark-secondary ring-1 ring-inset ring-primary-foreground/15 transition-colors duration-300 hover:text-primary-foreground hover:ring-primary-foreground/30 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/40">
            {HILERIQ_LABEL}
            <ArrowUpRight className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
          </HilerIQLink>
        </div>
      </div>

      {/* Primary navigation row */}
      <div className="mx-auto flex h-[84px] max-w-[1440px] items-center justify-between gap-8 px-6 lg:px-10">
        <Link to="/" className="flex-shrink-0" aria-label="Hiler Mortgage home">
          <img src={logoHorizontal} alt="Hiler Mortgage" className="h-16 w-auto xl:h-[72px]" width="257" height="72" />
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-7 2xl:gap-9 xl:flex" aria-label="Main navigation">
          {navLinks.map((item) => (
            <DropdownNav key={item.path} item={item} pathname={location.pathname} />
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <Button size="sm" variant="header" asChild>
            <ApplyLink>Apply Now</ApplyLink>
          </Button>

          <button
            className="xl:hidden text-primary-foreground/70 hover:text-primary-foreground transition-colors ml-1"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            {open ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          id="mobile-menu"
          className="border-t border-primary-foreground/8 bg-primary/95 backdrop-blur-2xl xl:hidden max-h-[calc(100vh-84px)] overflow-y-auto"
          role="dialog"
          aria-label="Mobile navigation menu"
        >
          <nav className="mx-auto max-w-[1440px] px-6 py-6 flex flex-col gap-1" aria-label="Mobile navigation">
            <HilerIQLink className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg border border-white/20 bg-white/15 px-4 py-3.5 font-body text-base font-semibold text-white transition-colors hover:bg-white/25">
              {HILERIQ_LABEL}
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </HilerIQLink>
            {navLinks.map((item) => (
              <div key={item.path}>
                <div className="flex items-center">
                  <Link
                    to={item.path}
                    onClick={() => setOpen(false)}
                    className={`flex-1 font-body text-base font-medium py-3 px-2 rounded-lg transition-colors ${
                      location.pathname === item.path
                        ? "text-primary-foreground bg-primary-foreground/5"
                        : "text-primary-foreground/60 hover:text-primary-foreground hover:bg-primary-foreground/5"
                    }`}
                  >
                    {item.navLabel ?? item.label}
                  </Link>
                  {item.children && (
                    <button
                      onClick={() => toggleMobileExpand(item.path)}
                      className="p-3 text-on-dark-tertiary hover:text-primary-foreground transition-colors"
                      aria-label={`${mobileExpanded === item.path ? "Collapse" : "Expand"} ${item.label} submenu`}
                      aria-expanded={mobileExpanded === item.path}
                    >
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileExpanded === item.path ? "rotate-180" : ""}`} aria-hidden="true" />
                    </button>
                  )}
                </div>
                {item.children && mobileExpanded === item.path && (
                  <div className="ml-4 mb-2 flex flex-col gap-0.5">
                    {item.children.map((child) => (
                      <div key={child.path}>
                        <Link
                          to={child.path}
                          onClick={() => setOpen(false)}
                          className={`py-2.5 px-3 rounded-lg transition-colors block ${
                            location.pathname === child.path
                              ? "bg-primary-foreground/5"
                              : "hover:bg-primary-foreground/5"
                          }`}
                        >
                          <span className={`block font-body text-[15px] font-medium ${
                            location.pathname === child.path
                              ? "text-primary-foreground"
                              : "text-primary-foreground/60"
                          }`}>
                            {child.label}
                          </span>
                          <span className="block font-body text-sm text-on-dark-disclosure mt-0.5 leading-snug">
                            {child.desc}
                          </span>
                        </Link>
                        {child.children && (
                          <div className="ml-4 flex flex-col gap-0.5">
                            {child.children.map((sub) => (
                              <Link
                                key={sub.path}
                                to={sub.path}
                                onClick={() => setOpen(false)}
                                className={`py-2 px-3 rounded-lg transition-colors ${
                                  location.pathname === sub.path
                                    ? "bg-primary-foreground/5"
                                    : "hover:bg-primary-foreground/5"
                                }`}
                              >
                                <span className={`font-body text-[14px] ${
                                  location.pathname === sub.path
                                    ? "text-primary-foreground"
                                    : "text-on-dark-secondary"
                                }`}>
                                  {sub.label}
                                </span>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      )}
    </header>
    </>
  );
};

export default Header;
